import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, DollarSign } from 'lucide-react';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import Loading from '../components/common/Loading';
import { Plan, PlanFormData } from '../types';
import api from '../services/api';

const PlanForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<PlanFormData>({
    nome: '',
    descricao: '',
    publish_plan: 0,
    valor: 0,
    status: 'ativo',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isEditMode) {
      loadPlan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPlan = async () => {
    try {
      setLoadingData(true);
      const response = await api.get<Plan>(`/plans/${id}`);
      const plan = response.data;

      setFormData({
        nome: plan.nome,
        descricao: plan.descricao || '',
        publish_plan: plan.publish_plan,
        valor: plan.valor,
        status: plan.status,
      });
    } catch (err: any) {
      setError('Erro ao carregar plano');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    let processedValue: string | number = value;

    // Convert valor to number
    if (name === 'valor') {
      processedValue = parseFloat(value) || 0;
    }

    if (name === 'publish_plan') {
      processedValue = parseFloat(value) || 0;
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (formData.valor <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      setError('Por favor, corrija os erros no formulário');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isEditMode) {
        await api.post(`/plans/${id}`, formData);
        setSuccess('Plano atualizado com sucesso');
      } else {
        await api.post('/plans', formData);
        setSuccess('Plano cadastrado com sucesso');
      }

      setTimeout(() => {
        navigate('/planos');
      }, 1500);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        (isEditMode ? 'Erro ao atualizar plano' : 'Erro ao cadastrar plano');
      setError(errorMessage);

      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <div className="mb-6">
        <Button
          variant="secondary"
          onClick={() => navigate('/planos')}
          className="mb-4"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Voltar
        </Button>

        <h1 className="text-3xl font-bold text-gray-900">
          {isEditMode ? 'Editar Plano' : 'Novo Plano'}
        </h1>
        <p className="text-gray-600">
          {isEditMode
            ? 'Atualize as informações do plano'
            : 'Preencha os dados para cadastrar um novo plano'}
        </p>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError('')} className="mb-6">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} className="mb-6">
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 space-y-6">
          {/* Nome do Plano */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações do Plano</h3>
            <Input
              label="Nome do Plano"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              error={errors.nome}
              required
              placeholder="Ex: Plano Básico"
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição
            </label>
            <textarea
              name="descricao"
              value={formData.descricao}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Descreva os benefícios e características do plano..."
            />
            {errors.descricao && (
              <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>
            )}
          </div>

          {/* Publicar */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status de Publicação <span className="text-red-500">*</span>
            </label>
            <select
              name="publish_plan"
              value={formData.publish_plan}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="1">Publicar</option>
              <option value="0">Não Publicar</option>
            </select>
            {errors.publish_plan && (
              <p className="mt-1 text-sm text-red-600">{errors.publish_plan}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Planos não publicados não aparecem na listagem pública
            </p>
          </div>

          {/* Valor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Valor Mensal <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="number"
                name="valor"
                value={formData.valor}
                onChange={handleChange}
                step="0.01"
                min="0"
                className={`pl-10 w-full px-3 py-2 border ${
                  errors.valor ? 'border-red-500' : 'border-gray-300'
                } rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="0.00"
                required
              />
            </div>
            {errors.valor && (
              <p className="mt-1 text-sm text-red-600">{errors.valor}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
            {errors.status && (
              <p className="mt-1 text-sm text-red-600">{errors.status}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Planos inativos não estarão disponíveis para novos associados
            </p>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/planos')}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button type="submit" loading={loading} disabled={loading}>
            <Save className="w-5 h-5 mr-2" />
            {isEditMode ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PlanForm;
